// =====================================================
// Swiss Finance OS - Realtime Status Indicator
// Visual indicator for realtime connection status
// =====================================================

import { useRealtimeContext } from './realtime-context';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface RealtimeStatusIndicatorProps {
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  variant?: 'badge' | 'icon' | 'full';
}

export function RealtimeStatusIndicator({
  size = 'sm',
  showLabel = false,
  variant = 'icon'
}: RealtimeStatusIndicatorProps) {
  const { isConnected, latestUpdate } = useRealtimeContext();

  const getIconSize = () => {
    switch (size) {
      case 'sm': return 'text-xs';
      case 'md': return 'text-sm';
      case 'lg': return 'text-base';
      default: return 'text-xs';
    }
  };

  const getIndicatorSize = () => {
    switch (size) {
      case 'sm': return 'w-2 h-2';
      case 'md': return 'w-3 h-3';
      case 'lg': return 'w-4 h-4';
      default: return 'w-2 h-2';
    }
  };

  const statusMessage = isConnected
    ? `Connected to realtime updates${latestUpdate ? ` • Last update: ${new Date(latestUpdate.timestamp).toLocaleTimeString()}` : ''}`
    : 'Disconnected from realtime updates';

  if (variant === 'badge') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant={isConnected ? 'default' : 'destructive'}
            className={`${getIconSize()} cursor-help`}
          >
            <i className={`fa-solid ${isConnected ? 'fa-wifi' : 'fa-wifi-slash'} mr-1`}></i>
            {showLabel && (isConnected ? 'Live' : 'Offline')}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{statusMessage}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (variant === 'icon') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative cursor-help">
            <i className={`fa-solid ${isConnected ? 'fa-wifi' : 'fa-wifi-slash'} ${getIconSize()} ${isConnected ? 'text-green-500' : 'text-red-500'}`}></i>
            <div className={`absolute -top-1 -right-1 ${getIndicatorSize()} rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{statusMessage}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (variant === 'full') {
    return (
      <div className="flex items-center gap-2">
        <div className="relative">
          <i className={`fa-solid ${isConnected ? 'fa-wifi' : 'fa-wifi-slash'} ${getIconSize()} ${isConnected ? 'text-green-500' : 'text-red-500'}`}></i>
          <div className={`absolute -top-1 -right-1 ${getIndicatorSize()} rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
        </div>
        {showLabel && (
          <span className={`text-sm ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
            {isConnected ? 'Live Updates' : 'Offline'}
          </span>
        )}
      </div>
    );
  }

  return null;
}